// eyeq — cabinet upload contracts (participant surface). Mirrors the machine
// upload flow (eyeq/upload.ts) with two differences enforced by ADR-0042:
// the uploader is ALWAYS the authenticated uid (no participant_id in any
// request), and the worker completes the MEQ metadata itself (camera from the
// stored EXIF, country/language from the asset's locale, filename from the
// original) — the participant fills only what a human actually knows.
import { z } from 'zod';
import { EyeqDhashSchema } from './assets';
import { EyeqLocaleSchema } from './locales';
import {
  MeqCategorySchema,
  MeqSubCategorySchema,
  MeqIndoorOutdoorSchema,
  MeqIndoorLightingSchema,
  MeqOutdoorLightingSchema,
} from './meta';

// ── POST /api/eyeq/cabinet/upload/init ──────────────────────────────────────
// Res reuses EyeqUploadInitResSchema (blob_url points at the cabinet blob route).
export const EyeqCabUploadInitReqSchema = z.object({
  items: z
    .array(
      z.object({
        filename: z.string().min(1),
        content_type: z.string().default(''),
        size: z.number().int().nonnegative(),
        locale: EyeqLocaleSchema, // must be one of the member's provisioned locales
        category: MeqCategorySchema,
        sub_category: MeqSubCategorySchema,
      }),
    )
    .min(1)
    .max(200),
});
export type EyeqCabUploadInitReq = z.infer<typeof EyeqCabUploadInitReqSchema>;

// PUT /api/eyeq/cabinet/upload/blob/:asset_id → EyeqBlobResSchema (upload.ts).
// POST /api/eyeq/cabinet/upload/archive?locale=&category=&sub_category= →
//   EyeqArchiveResSchema (upload.ts).

// ── POST /api/eyeq/cabinet/upload/submit ────────────────────────────────────
// The participant's slice of the MEQ metadata: what's on the frame and the
// shooting context. Everything else (camera_make/model, country, language,
// filename) is derived server-side; the assembled meta is validated against
// MeqAssetMetaSchema before indexing, so nothing invalid can land.
// url (2026-07-30, hybrid model): the participant is ASKED for the product
// page on url_required cells but an empty value never blocks — empties go to
// the staff backfill queue. Non-empty values pass eyeqUrlFormatNote both
// client-side (live) and in the server intake gate.
export const EyeqCabSubmitReqSchema = z.object({
  asset_id: z.string().min(1),
  subject_name: z.string().trim().min(1).max(200),
  disambiguation_details: z.string().trim().min(1).max(500),
  category: MeqCategorySchema,
  sub_category: MeqSubCategorySchema,
  indoor_outdoor: MeqIndoorOutdoorSchema,
  indoor_lighting: MeqIndoorLightingSchema,
  outdoor_lighting: MeqOutdoorLightingSchema,
  place_details: z.string().trim().min(1).max(500),
  has_text: z.boolean(),
  city: z.string().trim().max(120).default(''),
  url: z.string().trim().max(1000).nullable().optional(),
  // Perceptual hash from the client (see EyeqUploadSubmitReqSchema.dhash).
  dhash: EyeqDhashSchema.optional(),
});
export type EyeqCabSubmitReq = z.infer<typeof EyeqCabSubmitReqSchema>;
// Res reuses EyeqUploadSubmitResSchema (status + quota cell + categorical notes).

// GET /api/eyeq/cabinet/quota?locale= → EyeqQuotaResSchema (upload.ts); the
// locale must be one of the member's provisioned locales (managers: any).
